import { useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { AgentStatus } from '@algoboo/schema'
import type { AdminAgentRow } from '@algoboo/schema/api'
import { Button, Dialog, Field, Input, Pill, STATUS_PILL, Segmented, Select, Skeleton, formatCount, fullTime, shortAddress, timeAgo } from '@algoboo/ui'
import { adminAgentsQuery, adminKeys, useAdminAgents } from '../lib/admin'
import { errorCopy, isApiError, qk, useApiMutation } from '../lib/api'
import { useSession } from '../components/session'
import { AdminError, MoreButton, Table, Td } from '../components/admin/parts'
import { prefetchSignedIn } from '../lib/route-data'

export const Route = createFileRoute('/admin/agents')({
  validateSearch: z.object({ status: AgentStatus.optional() }),
  loaderDeps: ({ search }) => ({ status: search.status }),
  loader: ({ context: { queryClient: qc }, deps }) => prefetchSignedIn(qc, (me) => [me.isAdmin !== false && qc.ensureInfiniteQueryData(adminAgentsQuery(deps.status))]),
  component: AdminAgentsPage,
})

type Filter = 'all' | z.infer<typeof AgentStatus>

function AdminAgentsPage() {
  const { me } = useSession()
  const status = Route.useSearch().status
  const navigate = useNavigate({ from: '/admin/agents' })
  const q = useAdminAgents(status, !!me)
  const [moderating, setModerating] = useState<AdminAgentRow | null>(null)
  const items = q.data?.pages.flatMap((p) => p.items) ?? []
  return (
    <div className="space-y-4">
      <Segmented<Filter>
        label="Agent status"
        size="sm"
        value={status ?? 'all'}
        onChange={(v) => void navigate({ search: v === 'all' ? {} : { status: v }, replace: true })}
        options={[{ value: 'all', label: 'All' }, ...AgentStatus.options.map((s) => ({ value: s, label: STATUS_PILL[s].label }))]}
      />
      {q.isLoading ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : q.error ? (
        <AdminError error={q.error} refetch={() => void q.refetch()} />
      ) : (
        <>
          <Table head={['Agent', 'Status', 'Creator', 'Permissions', 'Subscribers', 'Created', '']} testId="admin-agents" caption="Agents, newest first">
            {items.length === 0 && (
              <tr>
                <Td className="py-6 text-ink-3">No agents.</Td>
              </tr>
            )}
            {items.map((r) => (
              <tr key={r.agent.id} data-testid="admin-agent" data-handle={r.agent.handle}>
                <Td>
                  <Link to="/a/$handle" params={{ handle: r.agent.handle }} className="font-medium text-ink hover:underline">
                    {r.agent.name}
                  </Link>{' '}
                  <span className="font-mono text-[12px] text-ink-3">@{r.agent.handle}</span>
                  {r.agent.kind === 'external' && <span className="ml-1.5 font-mono text-[11px] uppercase text-ink-3">ext</span>}
                </Td>
                <Td>
                  <Pill tone={STATUS_PILL[r.agent.status].tone}>{STATUS_PILL[r.agent.status].label}</Pill>
                </Td>
                <Td mono className="text-ink-2">
                  {r.creator.handle ? (
                    <Link to="/u/$handle" params={{ handle: r.creator.handle }} className="hover:underline">
                      @{r.creator.handle}
                    </Link>
                  ) : (
                    <span title={r.creator.primaryAddress}>{shortAddress(r.creator.primaryAddress)}</span>
                  )}
                </Td>
                <Td mono>{formatCount(r.permissionsActive)}</Td>
                <Td mono>{formatCount(r.subscribersCount)}</Td>
                <Td mono className="whitespace-nowrap text-ink-3">
                  <time dateTime={r.createdAt} title={fullTime(r.createdAt)}>
                    {timeAgo(r.createdAt)}
                  </time>
                </Td>
                <Td className="text-right">
                  <Button size="sm" variant="ghost" icon="shield" onClick={() => setModerating(r)}>
                    Moderate
                  </Button>
                </Td>
              </tr>
            ))}
          </Table>
          <MoreButton hasNext={q.hasNextPage} loading={q.isFetchingNextPage} onMore={() => void q.fetchNextPage()} />
        </>
      )}
      {moderating && <ModerateDialog row={moderating} onClose={() => setModerating(null)} />}
    </div>
  )
}

function ModerateDialog({ row, onClose }: { row: AdminAgentRow; onClose: () => void }) {
  const h = row.agent.handle
  const [status, setStatus] = useState<'active' | 'paused' | 'retired'>(row.agent.status === 'active' ? 'paused' : 'active')
  const [reason, setReason] = useState('')
  const m = useApiMutation('adminModerateAgent', { invalidate: () => [adminKeys.root(), qk.agent(h), qk.agents()], onSuccess: onClose })
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Moderate @${h}`}
      description="Sets the agent's status directly (pausing stops its ticks). The reason is logged with your user id."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant={status === 'retired' ? 'danger' : 'primary'} disabled={reason.trim().length === 0} loading={m.isPending} onClick={() => m.mutate({ params: { handle: h }, body: { status, reason: reason.trim() } })}>
            Set {status}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="New status" htmlFor="moderate-status">
          <Select id="moderate-status" value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
            <option value="active">active</option>
            <option value="paused">paused</option>
            <option value="retired">retired</option>
          </Select>
        </Field>
        <Field label="Reason" htmlFor="moderate-reason">
          <Input id="moderate-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why (kept in the API log)" />
        </Field>
        {m.error && (
          <p role="alert" className="text-[13px] text-danger">
            {isApiError(m.error) ? errorCopy(m.error.code).title : 'Could not moderate'}: {m.error.message}
          </p>
        )}
      </div>
    </Dialog>
  )
}
